<template>
  <div class="dashboard-pane" :class="[{ on: hasDomain, 'is-ga': isGaDashboard }, domain.shared]">
    <template v-if="hasDomain">
      <div class="dashboard-toggle-wrap" v-if="userData.userId === 'admin'">
        <div class="dashboard-toggle">
          <label class="toggle-label">
            <span>GA Dashboard</span>
            <input type="checkbox" :checked="isGaDashboard" @change="toggleGaDashboard" />
            <span class="toggle-switch"></span>
          </label>
        </div>
      </div>
      <dashboard-control
        :domains="domains"
        :domainIndex="domainIndex"
        @setCheckInstallSid="setCheckInstallSid"
        @setTrackingCodeSid="setTrackingCodeSid"
        @indexPrev="indexPrev"
        @indexNext="indexNext"
        @setIndex="setIndex"
      />
      <template v-if="!isGaDashboard">
        <DomainPaneHeader :domain="domain"
                          :twin-domain="twinDomain"
                          :mainDomain="mainDomain"
                          :basic-data="basicData"
                          :loaded="loaded || false"
                          :shared-count="sharedUserCount"
                          :is-analyzing="isAnalyzing"
                          @set-share="setShare"
                          @set-exact="toggleSetExact"
                          @getList="getList"
                          @domainUpdate="$set(domains, domainIndex, $event)"
                          @setMainDomain="setMainDomain"
                          @setDeletingDomain="setDeletingDomain"
                          @onTitleHeightChange="onTitleHeightChange"
        />
        <dashboard-control-bar
          :domain="domain"
          :disabled="!domain.installDate || isAnalyzing"
          :initial-journey-options="initialJourneyOptions"
          :initial-no-data="journeyNoDataFromParent"
          @resetInitialNoData="journeyNoDataFromParent = null"
        />
        <div class="not-installed" v-if="!domain.installDate">
          <i class="icon icon--alert"></i>
          <h5 v-html="$t('dashboard.notInstalled.title')"></h5>
          <p v-html="$t('dashboard.notInstalled.description')"></p>
          <div class="buttons">
            <ui-button @click="setTrackingCodeSid(true)">
              {{ $t('dashboard.trackingCode.codeGuide') }}
            </ui-button>
            <ui-button primary2 @click="setCheckInstallSid(true)">
              {{ $t('dashboard.trackingCode.checkInstall') }}
            </ui-button>
          </div>
        </div>
        <div class="not-installed" v-else-if="isAnalyzing">
          <i class="icon icon--coming-soon-red"></i>
          <h5 v-html="$t('dashboard.analyzing.title')"></h5>
          <p v-html="$t('dashboard.analyzing.description')"></p>
          <div class="progress">
            <div class="bar-wrap">
              <span class="bar" :style="{ width: analyzingPercent + '%' }"></span>
            </div>
            <span class="percent" :style="{ left: analyzingPercent + '%' }">
              {{ analyzingPercent | fixed(0) }}%
            </span>
          </div>
        </div>
        <domain-analysis v-else/>
      </template>
      <template v-else>
        <ga-dashboard />
      </template>
      <dimmed v-if="trackingCodeSid || checkInstallSid">
        <tracking-code v-if="trackingCodeSid"
                       :sid="trackingCodeSid"
                       @onClose="setTrackingCodeSid(null)" />
        <check-install v-if="checkInstallSid"
                       :sid="checkInstallSid"
                       @onClose="setCheckInstallSid(null)"
                       @onInstallSuccess="getList(checkInstallSid)" />
      </dimmed>
    </template>
    <first-entry v-else-if="loaded" />

    <dimmed v-if="deletingDomain">
      <delete-domain :domain="deletingDomain"
                     @onClose="setDeletingDomain(null)"
                     @onDeleteSuccess="getList" />
    </dimmed>

    <dimmed v-if="changingMainDomain">
      <main-domain :domain="changingMainDomain"
                   @onClose="setMainDomain(null)"
                   @onChangeMainDomain="changeMainDomain" />
    </dimmed>
    <Dimmed v-if="isSetShare" :click-to-close="true" @close="isSetShare = false">
      <SettingShare :pid="domain.parentSid" :user-id="userData.userId" :domain="domain" @close="completeShared" />
    </Dimmed>
    <Dimmed v-if="isSetExact" :click-to-close="true" @close="toggleSetExact">
      <SettingExact @close="toggleSetExact" @submit="setExactReport" />
    </Dimmed>
    <Dimmed v-if="openAlert">
      <div class="alert-success alert-container">
        <div class="alert-icon">
          <i class="icon icon--check-green-size50"></i>
        </div>
        <div class="alert-title" v-html="$t('share.dialogText.success.title')"></div>
        <div class="alert-description" v-html="$t('share.dialogText.success.description')"></div>
        <div class="alert-btn">
          <div class="btf-button outline" @click="openAlert = null">{{ $t('common.confirm') }}</div>
        </div>
      </div>
    </Dimmed>
    <Dimmed v-if="isChargingJam">
      <charge-pv
          :topUpJam="isChargingJam"
          :remainJam="remainJam"
          @closeLayer="isChargingJam = false" />
    </Dimmed>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { differenceInMinutes, parse, addDays, max, format } from 'date-fns'
import { getDomains, extendDomain, getGraphInfo, getSharedUserCount } from '@/api/domain'
import { EventBus } from '@/libs/event-bus'
import { buildExactAdParamConfig } from '@/libs/ad-param-config'
import { compare09aAzZ, getOffset, utcToLocalTimezone } from '@/libs/utils'
import FirstEntry from '@/components/dashboard/FirstEntry'
import DashboardControl from '@/components/dashboard/DashboardControl'
import DashboardControlBar from '@/components/dashboard/DashboardControlBar'
import DomainPaneHeader from '@/components/dashboard/DomainPaneHeader'
import TrackingCode from '@/components/dashboard/TrackingCode'
import CheckInstall from '@/components/dashboard/CheckInstall'
import DeleteDomain from '@/components/dashboard/DeleteDomain'
import MainDomain from '@/components/dashboard/MainDomain'
import Dimmed from '@/components/ui/Dimmed'
import SettingShare from '@/components/dashboard/SettingShare.vue'
import SettingExact from '@/components/dashboard/SettingExact.vue'
import DomainAnalysis from '@/components/dashboard/DomainAnalysis.vue'
import UiButton from '@/components/ui/UiButton'
import ChargePv from '@/components/user-info/manage-plan/ChargePv'
import GaDashboard from '@/components/dashboard/GaDashboard'
export default {
  name: 'DashboardPane',
  components: {
    FirstEntry,
    DashboardControl,
    DashboardControlBar,
    TrackingCode,
    CheckInstall,
    DeleteDomain,
    MainDomain,
    Dimmed,
    DomainPaneHeader,
    SettingShare,
    SettingExact,
    DomainAnalysis,
    UiButton,
    ChargePv,
    GaDashboard
  },
  data () {
    return {
      loaded: false,
      domainOnTop: false,
      domains: [],
      domainIndex: 0,
      mainDomain: null,
      changingMainDomain: null,
      deletingDomain: null,
      trackingCodeSid: null,
      checkInstallSid: null,
      basicData: {},
      isSetShare: false,
      isSetExact: false,
      openAlert: null,
      sharedUserCount: 0,
      isTitleHeightOver: false,
      defaultDateRange: -6,
      isChargingJam: false,
      remainJam: 0,
      gaSid: '56e7142775',
      gaDomain: null,
      isGaDashboard: false,
      journeyOptionsFromParent: null,
      journeyPeriodFromParent: null,
      journeyNoDataFromParent: null
    }
  },
  computed: {
    ...mapState([
      'userData',
      'journeyOptions',
      'journeyPeriod',
      'domain'
    ]),
    initialJourneyOptions () {
      return {
        sid: this.domain ? this.domain.orgSid : null,
        matchType: this.domain ? this.domain.matchType : null,
        device: null,
        exist: null,
        refer: null,
        aiRefererIds: null,
        granularity: 'day',
        utm: null,
        survey: null
      }
    },
    twinDomain () {
      return this.domains.find(domain => domain.orgSid === this.domain.twinSid)
    },
    hasDomain () {
      return this.domains.length > 0
    },
    isAnalyzing () {
      return this.domain.uiState === 'analyzing'
    },
    analyzingPercent () {
      const today = new Date()
      const installDate = this.domain.installDate || this.domain.regDate
      const diff = differenceInMinutes(today, installDate)
      const percent = diff / 120 * 100
      return Math.max(1, Math.min(99, percent))
    }
  },
  created () {
    this.journeyOptionsFromParent = window._journeyOptionsFromParent ? { ...window._journeyOptionsFromParent } : null
    this.journeyPeriodFromParent = window._journeyPeriodFromParent ? { ...window._journeyPeriodFromParent } : null
    this.journeyNoDataFromParent = window._journeyNoDataFromParent !== undefined ? window._journeyNoDataFromParent : null
    window._journeyOptionsFromParent = null
    window._journeyPeriodFromParent = null
    window._journeyNoDataFromParent = null
    this.setJourneyOptions({ ...this.initialJourneyOptions })
    this.getList()
    EventBus.$on('onChargeJam', (remainJam) => {
      this.remainJam = remainJam
      this.isChargingJam = true
    })
  },
  methods: {
    ...mapMutations([
      'setDomain',
      'setJourneyOptions',
      'setJourneyPeriod'
    ]),
    onTitleHeightChange (isTitleHeightOver) {
      this.isTitleHeightOver = isTitleHeightOver
    },
    setTrackingCodeSid (value) {
      this.trackingCodeSid = value ? this.domain.orgSid : null
    },
    setCheckInstallSid (value) {
      this.checkInstallSid = value ? this.domain.sid : null
    },
    setDeletingDomain (value) {
      this.deletingDomain = value
    },
    setMainDomain (value) {
      if (!value || value.orgSid !== this.mainDomain) {
        this.changingMainDomain = value
      }
    },
    changeMainDomain () {
      this.getList(this.changingMainDomain.orgSid, true)
    },
    indexPrev () {
      if (this.domainIndex > 0) {
        this.setIndex(this.domainIndex - 1)
      }
    },
    indexNext () {
      if (this.domainIndex < this.domains.length - 1) {
        this.setIndex(this.domainIndex + 1)
      }
    },
    setIndex (index) {
      this.domainIndex = index

      this.setDomainData()
    },
    getList (sidShown) {
      EventBus.$emit('onLoading', true)
      const querySid = this.$route.query.sid
      let currentSid = sidShown || querySid || null

      getDomains(currentSid, true).then(result => {
        result.sort(compare09aAzZ)
        let currentIndex = 0
        let idx = 0
        let sidFound = false
        this.domains = []

        for (const domain of result) {
          if (domain.mainDomain && domain.shared !== 'shared') {
            domain.timezone = getOffset(domain.timezoneName)
            this.domains.push(domain)
            this.mainDomain = domain.orgSid
            if (currentSid && [domain.orgSid, domain.sid].includes(currentSid)) {
              sidFound = true
            }
            idx++
            break
          }
        }

        for (const domain of result) {
          if (domain.orgSid === this.gaSid) {
            this.gaDomain = domain
          } else if (!domain.mainDomain || domain.shared === 'shared') {
            this.domains.push(domain)
            if (currentSid && [domain.orgSid, domain.sid].includes(currentSid)) {
              sidFound = true
              currentIndex = idx
            }
            idx++
          }
        }

        if (currentSid === this.gaSid) {
          this.toggleGaDashboard()
        } else {
          if (currentSid && !sidFound) {
            this.domains = []
            EventBus.$emit('onError', { error: 'Domain not found' }, 404)
            return
          }
          this.setIndex(currentIndex)
        }

        this.loaded = true

        if (querySid) {
          this.$router.replace({
            query: { ...this.$route.query, sid: undefined }
          })
        }

        EventBus.$emit('onLoading', false)
      })
    },
    setDomainData () {
      if (!this.hasDomain) return

      this.setDomain(this.domains[this.domainIndex] || '')
      this.setJourneyOptions({ ...this.initialJourneyOptions, ...(this.journeyOptionsFromParent || {}) })
      this.journeyOptionsFromParent = null

      this.setPeriod()
      this.getSharedUserList()

      const sid = this.domain.orgSid || this.domain.sid
      getGraphInfo({ sid }, (this.$route.query.sid === sid)).then(result => {
        this.basicData = result.month
      })
    },
    setPeriod () {
      const now = utcToLocalTimezone(new Date(), this.domain.timezoneName)
      const periodMin = utcToLocalTimezone(max(parse(this.domain.installDate || '1970-01-01'), addDays(now, -397)), this.domain.timezoneName)
      const periodMax = now

      let periodStart = now
      let periodEnd = now
      if (this.journeyPeriodFromParent && this.journeyPeriodFromParent.start && this.journeyPeriodFromParent.end) {
        periodStart = parse(this.journeyPeriodFromParent.start)
        periodEnd = parse(this.journeyPeriodFromParent.end)
        this.journeyPeriodFromParent = null
      } else if (this.domain.installDate && !this.isAnalyzing) {
        periodStart = max(periodMin, addDays(periodEnd, this.defaultDateRange))
      }

      this.setJourneyPeriod({
        start: format(periodStart, 'YYYY-MM-DD'),
        end: format(periodEnd, 'YYYY-MM-DD'),
        min: format(periodMin, 'YYYY-MM-DD'),
        max: format(periodMax, 'YYYY-MM-DD')
      })
    },
    setShare () {
      this.isSetShare = true
    },
    completeShared (value) {
      this.isSetShare = false
      if (value === 'success') {
        this.openAlert = value
      }
    },
    toggleSetExact () {
      this.isSetExact = !this.isSetExact
    },
    setExactReport ({ reportName, isToggleOn, customParameters }) {
      EventBus.$emit('onLoading', true)
      const requestData = {
        domainName: reportName,
        parentSid: this.domain.orgParentSid,
        adParamConfig: buildExactAdParamConfig({
          enabled: isToggleOn,
          customAdParams: customParameters
        })
      }
      extendDomain(requestData)
        .then(res => {
          this.toggleSetExact()
          this.getList(res.orgSid)
          EventBus.$emit('onLoading', false)
        })
        .catch(e => {
          console.log(e)
        })
    },
    getSharedUserList () {
      if (!this.domain || !this.domain.parentSid) return
      const requestData = {
        parentSid: this.domain.parentSid
      }

      getSharedUserCount(requestData).then(res => {
        this.sharedUserCount = res
      })
    },
    toggleGaDashboard () {
      if (this.isGaDashboard) {
        this.setDomainData()
      } else {
        this.setDomain({ ...this.gaDomain, isGa: true })
        this.setJourneyOptions({ ...this.initialJourneyOptions })
      }
      this.isGaDashboard = !this.isGaDashboard
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-pane {
  display: flex;
  flex-direction: column;
  position: static;
  width: 100%;
  &.on {
    position: relative;
    margin-top: 33px;
    margin-bottom: 20px;
  }
  &.is-ga {
    /deep/ .dashboard-control {
      justify-content: flex-end;
      height: 42px;
      .navigation--area {
        display: none;
      }
    }
  }
  .pane {
    position: relative;
    width: 100%;
  }
}

.dashboard-toggle-wrap {
  margin-top: -15px;
  padding: 0 10px 20px 10px;
}

.dashboard-toggle {
  display: inline-block;
  .toggle-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    color: #444;

    input {
      display: none;
    }

    .toggle-switch {
      position: relative;
      width: 36px;
      height: 20px;
      background: #ccc;
      border-radius: 12px;
      transition: background 0.2s;

      &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        background: #fff;
        border-radius: 50%;
        transition: transform 0.2s;
      }
    }

    input:checked + .toggle-switch {
      background: $brandColor;

      &::after {
        transform: translateX(16px);
      }
    }
  }
}

.not-installed {
  @include flexXYcenter;
  flex-direction: column;
  flex: 1;
  margin-bottom: 60px;
  h5 {
    color: #444444;
    font-size: 20px;
    font-weight: 500;
    margin: 10px 0 20px;
    height: auto;
  }

  p {
    color: #666666;
    line-height: 1.57;
    font-size: 14px;
    text-align: center;
  }

  .buttons {
    margin-top: 27px;

    .ui-btn {
      margin: 0 2px;
    }
  }

  .progress {
    position: relative;
    width: 240px;
    margin-top: 20px;

    .bar-wrap {
      height: 10px;
      border-radius: 100px;
      background-color: #d7d7d7;
      overflow: hidden;
    }

    .bar {
      display: block;
      min-width: 10px;
      height: 10px;
      background-color: #ec0047;
    }

    .percent {
      font-weight: 500;
      position: absolute;
      bottom: -22px;
      font-size: 13px;
      color: #ec0047;
      transform: translateX(-50%);
    }
  }
}

.alert-container {
  position: relative;
  border-radius: 14px;
  background: #FFF;
  box-shadow: 0px 8px 15px 0px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(0, 0, 0, 0.16);
  padding: 50px;
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  // justify-content: center;
  align-items: center;
  gap: 12px;
  width: 330px;
  // min-height: 238px;
  .alert-close {
    position: absolute;
    top: 10px;
    right: 10px;
  }
}
.alert-success {
  color: #2F2F2F;

  font-size: 14px;
  line-height: 1.5;
  .alert-title {
    color: #444;
    font-size: 20px;
    font-weight: 500;
    text-align: center;
  }
  .alert-description {
    padding-bottom: 45px;
  }
}
.shared /deep/{
  .svy, .state-switch, .button-wrap, .goto .mine {
    opacity: 0.3;
    pointer-events: none
  }
}
</style>
